// @ts-nocheck
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Trophy, ChevronLeft } from "lucide-react";
import { getSubdomain } from "@/utils/subdomain";

export default function CircuitStandingsPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const subdomain = getSubdomain();
  // ?org= fallback used when there is no live org subdomain (preview/apex).
  const orgParam = searchParams.get("org");

  // Resolve the org for the current subdomain so slugs (which are only
  // unique per-org) can be disambiguated. Null on apex/preview/local.
  const { data: subdomainOrgId, isLoading: orgLoading } = useQuery({
    queryKey: ["circuit-org-by-subdomain", subdomain],
    enabled: subdomain !== null,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("organizations")
        .select("id")
        .eq("subdomain", subdomain)
        .maybeSingle();
      return data?.id ?? null;
    },
  });

  const orgId = subdomainOrgId ?? orgParam ?? null;

  const { data: circuit, isLoading } = useQuery({
    queryKey: ["public-circuit", slug, orgId ?? subdomain],
    // Wait for subdomain org resolution so we don't grab the wrong org's
    // circuit with the same slug.
    enabled: !!slug && (subdomain === null || !orgLoading),
    queryFn: async () => {
      let query = (supabase as any)
        .from("circuits")
        .select("*")
        .eq("slug", slug)
        .eq("is_public", true);
      if (orgId) query = query.eq("organization_id", orgId);
      const { data } = await query.limit(1).maybeSingle();
      return data;
    },
  });

  const { data: standings = [] } = useQuery({
    queryKey: ["public-circuit-standings", circuit?.id],
    enabled: !!circuit?.id,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("circuit_ranking_points")
        .select("team_id, team_name, points_awarded, result")
        .eq("circuit_id", circuit.id);
      const map = new Map<string, any>();
      (data || []).forEach((r: any) => {
        const key = r.team_id || r.team_name;
        const cur = map.get(key) || {
          team_name: r.team_name,
          points: 0,
          wins: 0,
          losses: 0,
          games: 0,
        };
        cur.points += Number(r.points_awarded || 0);
        cur.games += 1;
        if (r.result === "win") cur.wins += 1;
        if (r.result === "loss") cur.losses += 1;
        map.set(key, cur);
      });
      return Array.from(map.values()).sort((a, b) => b.points - a.points);
    },
  });

  const { data: events = [] } = useQuery({
    queryKey: ["public-circuit-events", circuit?.id],
    enabled: !!circuit?.id,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("circuit_event_links")
        .select("event_id, is_live_period, events(id,name,start_date,slug,status)")
        .eq("circuit_id", circuit.id);
      return (data || []).filter((l: any) => l.events?.status !== "draft");
    },
  });

  if (isLoading || (subdomain !== null && orgLoading)) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!circuit) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold mb-2">Circuit not found</h1>
        <p className="text-muted-foreground mb-6">
          This circuit may have been removed or is not yet public.
        </p>
        <Link to="/circuits" className="text-primary hover:underline">
          ← Back to all circuits
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-10 max-w-5xl">
      <Link
        to="/circuits"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft className="w-4 h-4" /> All circuits
      </Link>

      <header className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Trophy className="w-7 h-7 text-primary" /> {circuit.name}
        </h1>
        <p className="text-muted-foreground mt-2">
          {[circuit.circuit_type, circuit.age_group, circuit.gender, circuit.season]
            .filter(Boolean)
            .join(" · ")}
        </p>
        {circuit.description && (
          <p className="mt-4 max-w-2xl">{circuit.description}</p>
        )}
      </header>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 border rounded-xl bg-card overflow-hidden">
          <div className="p-4 border-b font-semibold">Standings</div>
          {standings.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              Standings will appear once games are played.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">#</th>
                  <th className="px-4 py-2 text-left font-medium">Team</th>
                  <th className="px-4 py-2 text-right font-medium">GP</th>
                  <th className="px-4 py-2 text-right font-medium">W-L</th>
                  <th className="px-4 py-2 text-right font-medium">Points</th>
                </tr>
              </thead>
              <tbody>
                {standings.map((s: any, i: number) => (
                  <tr key={i} className="border-t">
                    <td className="px-4 py-2">{i + 1}</td>
                    <td className="px-4 py-2 font-medium">{s.team_name}</td>
                    <td className="px-4 py-2 text-right text-muted-foreground">
                      {s.games}
                    </td>
                    <td className="px-4 py-2 text-right text-muted-foreground">
                      {s.wins}-{s.losses}
                    </td>
                    <td className="px-4 py-2 text-right font-bold">
                      {s.points.toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border rounded-xl bg-card">
          <div className="p-4 border-b font-semibold">Events</div>
          {events.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">
              No events scheduled yet.
            </div>
          ) : (
            <ul className="divide-y">
              {events.map((l: any) => (
                <li key={l.event_id} className="p-4">
                  <Link
                    to={`/events/${l.events?.slug || l.event_id}`}
                    className="font-medium text-sm hover:underline"
                  >
                    {l.events?.name}
                  </Link>
                  <div className="text-xs text-muted-foreground mt-1">
                    {l.events?.start_date}
                    {l.is_live_period && (
                      <span className="ml-2 text-primary font-semibold">LIVE</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
